/**
 * CctvPipHost — 공장 CCTV 라이브 창을 앱 전체에서 한 번만 그린다(Layout 에 둔다).
 *
 *   · 진입점은 출하조회 CctvPanel 썸네일(openCctvPip). 창 상태는 hooks/useCctvPip.ts.
 *   · 최소화 창은 다른 페이지로 옮겨도 그대로 떠 있고 영상도 끊기지 않는다.
 *   · 큰 창(화면 중앙)은 페이지를 옮기면 닫는다 — 창 밖 클릭 시 닫히는 것과 같은 규칙.
 *   · 법인·사업부를 바꿔 CCTV 설정이 꺼지면 닫는다.
 *   · 플레이어 코드는 창을 열 때만 불러온다(Layout 첫 번들에 싣지 않음).
 */
import { lazy, Suspense, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCameraConfig } from "@/hooks/useCameraConfig";
import { closeCctvPip, setCctvPipMini, useCctvPip } from "@/hooks/useCctvPip";
import { cameraReady } from "@/domain/camera";

const CctvPip = lazy(() => import("./CctvPip").then((m) => ({ default: m.CctvPip })));

export function CctvPipHost() {
  const { camId, seq, mini } = useCctvPip();

  // 페이지 이동 — 큰 창이면 닫고, 최소화 창이면 유지
  const { pathname } = useLocation();
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    if (!mini) closeCctvPip();
  }, [pathname, mini]);

  // 로그아웃 등으로 Layout 이 내려가면 다음 진입 때 다시 뜨지 않게 닫아 둔다
  useEffect(() => closeCctvPip, []);

  if (!camId) return null;
  return <CctvPipOpen camId={camId} seq={seq} />;
}

/** 창이 열려 있을 때만 CCTV 설정을 구독 */
function CctvPipOpen({ camId, seq }: { camId: string; seq: number }) {
  const { config, loaded } = useCameraConfig();
  const ready = cameraReady(config);

  useEffect(() => {
    if (loaded && !ready) closeCctvPip();
  }, [loaded, ready]);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <CctvPip
        config={config}
        initialCamId={camId}
        openSeq={seq}
        onMiniChange={setCctvPipMini}
        onClose={closeCctvPip}
      />
    </Suspense>
  );
}
